import {
  roleAtLeast,
  type BlockRule,
  type BlockRuleOrigin,
  type EffectiveBlockRule,
  type ConfigScopeRef,
} from '@bartizo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import {
  getBlockRuleBans,
  getBlockRules,
  getServerBlockRules,
  promoteBlockRule,
  removeBlockRule,
  saveBlockRule,
} from '../api/block-rules.js';
import { getServer, listServerGroups, listServers } from '../api/servers.js';
import { useCurrentUser } from '../auth/session-context.js';
import { BlockRuleDialog } from '../components/BlockRuleDialog.js';
import { describeRule } from '../components/block-rule-text.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { EmptyState } from '../components/EmptyState.js';
import { PageHeader } from '../components/PageHeader.js';
import { RuleDetail } from '../components/RuleDetail.js';
import { formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { DataTable } from '../ui/DataTable.js';
import { SelectField } from '../ui/Field.js';
import { Loading } from '../ui/Skeleton.js';

/**
 * Block rules (knowledge doc section 12, "Block rules"; P11.5).
 *
 * One layer at a time — global, a group, or a server — with its rules and, for
 * a server, what each of its sites ends up running and where each rule came
 * from. Lower wins: a server's rule replaces the group's and the fleet's of the
 * same id, and a site's copy replaces it for that site only. Anyone signed in
 * reads; operators change (section 12's Medium row), one rule at a time.
 */

const GLOBAL = 'global';

const refOf = (choice: string): ConfigScopeRef => {
  if (choice.startsWith('group:')) return { scope: 'group', id: choice.slice(6) };
  if (choice.startsWith('server:')) return { scope: 'server', id: choice.slice(7) };
  return { scope: 'global' };
};

const errorText = (caught: unknown, fallback: string): string =>
  caught instanceof Error ? caught.message : fallback;

function ModeBadge({ rule }: { rule: BlockRule }): ReactNode {
  if (!rule.enabled) return <span className="badge badge-muted">Off</span>;
  return rule.mode === 'watch' ? (
    <span className="badge badge-warning">Watching</span>
  ) : (
    <span className="badge badge-success">Enforced</span>
  );
}

/**
 * What CrowdSec counted for a behaviour rule since it last started, under its ban
 * count: a rule that is armed and has seen nothing reads differently from one
 * that is counting and has not yet fired. Said only when servers report at all.
 */
function CountingNote({
  rule,
  counting,
  reporting,
}: {
  rule: BlockRule;
  counting: { counted: number; fired: number } | undefined;
  reporting: number;
}): ReactNode {
  if (rule.kind !== 'behaviour' || !rule.enabled || reporting === 0) return null;
  const title = 'From CrowdSec on the servers; counted again from zero whenever it restarts';
  if (counting === undefined) {
    return (
      <span className="cell-sub" title={title}>
        Armed · nothing matched yet
      </span>
    );
  }
  const noun = rule.mode === 'watch' ? 'watched' : 'counted';
  return (
    <span className="cell-sub" title={title}>
      {counting.counted.toLocaleString()} request{counting.counted === 1 ? '' : 's'} {noun} ·{' '}
      {counting.fired === 0
        ? `none reached ${String(rule.count)} in ${String(rule.within_seconds)}s`
        : `${counting.fired.toLocaleString()} reached ${String(rule.count)} in ${String(rule.within_seconds)}s`}
    </span>
  );
}

const originLabel = (origin: BlockRuleOrigin): string =>
  origin.scope === 'global'
    ? 'Every server'
    : origin.scope === 'group'
      ? `Group ${origin.name ?? ''}`
      : 'This server';

type Editing =
  | {
      kind: 'new';
      prefill?: {
        path?: string | undefined;
        site?: string | undefined;
        mode?: 'watch' | undefined;
      };
    }
  | { kind: 'edit'; rule: BlockRule }
  | { kind: 'site'; rule: BlockRule; site: string };

export function RulesPage(): ReactNode {
  const session = useCurrentUser();
  const canEdit = roleAtLeast(session.user.role, 'operator');
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const group = params.get('group');
  const server = params.get('server');
  const choice = group !== null ? `group:${group}` : server !== null ? `server:${server}` : GLOBAL;
  const ref = refOf(choice);

  const groups = useQuery({ queryKey: ['server-groups'], queryFn: listServerGroups });
  const servers = useQuery({
    queryKey: ['servers', { limit: 100 }],
    queryFn: () => listServers({ limit: 100 }),
  });
  const key = ['block-rules', choice];
  const layer = useQuery({ queryKey: key, queryFn: () => getBlockRules(ref) });
  const bans = useQuery({ queryKey: ['block-rule-bans'], queryFn: getBlockRuleBans });
  const bansOf = (rule: BlockRule) =>
    bans.data?.items.find((each) => each.rule === `${rule.kind}:${rule.id}`);
  const countingOf = (rule: BlockRule) =>
    bans.data?.counting.find((each) => each.rule === `${rule.kind}:${rule.id}`);
  const effective = useQuery({
    queryKey: ['server-block-rules', server],
    queryFn: () => getServerBlockRules(server ?? ''),
    enabled: server !== null,
  });

  // The Traffic page's "Make a rule" arrives with a path, and its site (P11.7).
  const [editing, setEditing] = useState<Editing | null>(() => {
    const path = params.get('path');
    if (path === null) return null;
    const site = params.get('site');
    return {
      kind: 'new',
      prefill: {
        path,
        ...(site === null ? {} : { site }),
        ...(params.get('mode') === 'watch' ? { mode: 'watch' as const } : {}),
      },
    };
  });
  const [removing, setRemoving] = useState<BlockRule | null>(null);
  const [moving, setMoving] = useState<BlockRule | null>(null);
  // A server's rule moves to its group, if it has one, or to global.
  const serverDetail = useQuery({
    queryKey: ['server', server],
    queryFn: () => getServer(server ?? ''),
    enabled: server !== null,
  });
  const wider: { ref: ConfigScopeRef; label: string }[] = [
    ...(ref.scope === 'server' && serverDetail.data?.group != null
      ? [
          {
            ref: { scope: 'group' as const, id: serverDetail.data.group.id },
            label: `its group, ${serverDetail.data.group.name}`,
          },
        ]
      : []),
    ...(ref.scope === 'global'
      ? []
      : [{ ref: { scope: 'global' as const }, label: 'every server' }]),
  ];
  const [target, setTarget] = useState(0);
  const [done, setDone] = useState<string | null>(null);

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['block-rules'] });
    void queryClient.invalidateQueries({ queryKey: ['server-block-rules'] });
    void queryClient.invalidateQueries({ queryKey: ['config-rollout'] });
  };
  const reached = (created: number): string =>
    created === 0
      ? 'Saved. No server needed new settings.'
      : `Saved. ${String(created)} server${created === 1 ? '' : 's'} will apply it on their next check.`;

  const save = useMutation({
    mutationFn: (rule: BlockRule) =>
      saveBlockRule(editing?.kind === 'site' ? { scope: 'server', id: server ?? '' } : ref, rule),
    onSuccess: (response) => {
      setEditing(null);
      setDone(reached(response.rebuild.created));
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (rule: BlockRule) => removeBlockRule(ref, rule),
    onSuccess: (response) => {
      setRemoving(null);
      setDone(reached(response.rebuild.created));
      refresh();
    },
  });

  const move = useMutation({
    mutationFn: (rule: BlockRule) => {
      const to = wider[target]?.ref ?? { scope: 'global' as const };
      return promoteBlockRule(ref, to, rule);
    },
    onSuccess: (response) => {
      setMoving(null);
      setDone(reached(response.rebuild.created));
      refresh();
    },
  });

  const choose = (value: string): void => {
    setDone(null);
    if (value.startsWith('group:')) setParams({ group: value.slice(6) });
    else if (value.startsWith('server:')) setParams({ server: value.slice(7) });
    else setParams({});
  };

  return (
    <div className="page">
      <PageHeader
        title="Rules"
        description="What the web servers refuse, and what CrowdSec bans for. Global rules apply first, then a group's, then a server's; a site's own copy wins there."
      />

      <Card title="Which layer">
        <SelectField
          label="Layer"
          value={choice}
          onChange={(event) => {
            choose(event.target.value);
          }}
        >
          <option value={GLOBAL}>Global — every server</option>
          {(groups.data ?? []).map((each) => (
            <option key={each.id} value={`group:${each.id}`}>
              Group {each.name}
            </option>
          ))}
          {(servers.data?.items ?? []).map((each) => (
            <option key={each.id} value={`server:${each.id}`}>
              Server {each.name}
            </option>
          ))}
        </SelectField>
      </Card>

      {done !== null && <Alert tone="success">{done}</Alert>}

      <Card
        title={
          layer.data?.name === null || layer.data === undefined
            ? 'Rules'
            : `Rules for ${layer.data.name}`
        }
        {...(layer.data === undefined
          ? {}
          : {
              description: `Reaches ${String(layer.data.affectedServers)} server${layer.data.affectedServers === 1 ? '' : 's'}.`,
            })}
        {...(canEdit
          ? {
              aside: (
                <Button
                  variant="primary"
                  onClick={() => {
                    save.reset();
                    setEditing({ kind: 'new' });
                  }}
                >
                  New rule
                </Button>
              ),
            }
          : {})}
      >
        {layer.isPending && <Loading />}
        {layer.isError && (
          <Alert tone="danger" title="Could not load the rules">
            {errorText(layer.error, 'Try again in a moment.')}
          </Alert>
        )}
        {layer.data?.rules.length === 0 && (
          <EmptyState title="No rules in this layer">
            Start from a preset — secret files, PHP scanners, WordPress login guessing — or write
            your own. Watch a new rule for a day before enforcing it.
          </EmptyState>
        )}
        {layer.data !== undefined && layer.data.rules.length > 0 && (
          <DataTable
            caption="Rules in this layer"
            columns={[
              {
                key: 'rule',
                header: 'Rule',
                rowHeader: true,
                sortValue: (rule) => rule.name,
                cell: (rule) => (
                  <>
                    {rule.name}
                    <span className="cell-sub">
                      {rule.kind === 'request'
                        ? 'Request'
                        : rule.kind === 'trigger'
                          ? 'CrowdSec trigger'
                          : 'Behaviour'}{' '}
                      · {rule.id}
                    </span>
                  </>
                ),
              },
              {
                key: 'site',
                header: 'Site',
                sortValue: (rule) => rule.site ?? '',
                cell: (rule) => rule.site ?? <span className="muted">Every site</span>,
              },
              { key: 'does', header: 'Does', cell: (rule) => describeRule(rule) },
              {
                key: 'bans',
                header: `Bans, ${String(bans.data?.days ?? 30)} days`,
                align: 'end' as const,
                sortValue: (rule: BlockRule) => bansOf(rule)?.bans ?? 0,
                cell: (rule: BlockRule) => {
                  if (rule.kind === 'request') {
                    return (
                      <span
                        className="muted"
                        title="A request rule refuses requests; it bans no one"
                      >
                        —
                      </span>
                    );
                  }
                  const found = bansOf(rule);
                  if (bans.isPending) return <span className="muted">…</span>;
                  if (found === undefined) {
                    return (
                      <>
                        <span className="muted">0</span>
                        <CountingNote
                          rule={rule}
                          counting={countingOf(rule)}
                          reporting={bans.data?.reporting ?? 0}
                        />
                      </>
                    );
                  }
                  return (
                    <>
                      <strong>{found.bans.toLocaleString()}</strong>
                      <span className="cell-sub">
                        {found.addresses.toLocaleString()} address
                        {found.addresses === 1 ? '' : 'es'} · {found.servers} server
                        {found.servers === 1 ? '' : 's'} · last {formatRelative(found.lastSeen)}
                      </span>
                    </>
                  );
                },
              },
              { key: 'state', header: 'State', cell: (rule) => <ModeBadge rule={rule} /> },
              ...(canEdit
                ? [
                    {
                      key: 'act',
                      header: 'Next',
                      cell: (rule: BlockRule) => (
                        <span className="row">
                          <Button
                            variant="ghost"
                            onClick={() => {
                              remove.reset();
                              setRemoving(rule);
                            }}
                          >
                            Remove
                          </Button>
                          {wider.length > 0 && (
                            <Button
                              variant="ghost"
                              onClick={() => {
                                move.reset();
                                setTarget(0);
                                setMoving(rule);
                              }}
                            >
                              Move up
                            </Button>
                          )}
                        </span>
                      ),
                    },
                  ]
                : []),
            ]}
            rows={layer.data.rules}
            rowKey={(rule) => `${rule.site ?? ''}|${rule.id}`}
            renderDetail={(rule) => <RuleDetail rule={rule} />}
            {...(canEdit
              ? {
                  onSelect: (rule: BlockRule) => {
                    save.reset();
                    setEditing({ kind: 'edit', rule });
                  },
                }
              : {})}
          />
        )}
      </Card>

      {server !== null && (
        <Card
          title="What each site runs"
          description="Every rule in force on this server, the layer it came from, and each site's own copies."
        >
          {effective.isError && (
            <Alert tone="danger">{errorText(effective.error, 'Could not load them.')}</Alert>
          )}
          {effective.data?.sites.map((site) => (
            <div key={site.host ?? '*'} className="stack">
              <h3>{site.host ?? 'Every site without copies of its own'}</h3>
              {site.rules.length === 0 ? (
                <p className="muted text-sm">No rules.</p>
              ) : (
                <DataTable
                  caption={`Rules for ${site.host ?? 'every other site'}`}
                  columns={[
                    {
                      key: 'rule',
                      header: 'Rule',
                      rowHeader: true,
                      cell: ({ rule }) => rule.name,
                    },
                    { key: 'does', header: 'Does', cell: ({ rule }) => describeRule(rule) },
                    {
                      key: 'from',
                      header: 'From',
                      cell: ({ rule, origin }) =>
                        `${originLabel(origin)}${rule.site === undefined ? '' : ', this site'}`,
                    },
                    {
                      key: 'state',
                      header: 'State',
                      cell: ({ rule }) => <ModeBadge rule={rule} />,
                    },
                    ...(canEdit
                      ? [
                          {
                            key: 'act',
                            header: 'Next',
                            cell: ({ rule }: EffectiveBlockRule) => (
                              <Button
                                variant="ghost"
                                onClick={() => {
                                  save.reset();
                                  setEditing({ kind: 'site', rule, site: site.host ?? '' });
                                }}
                              >
                                {site.host === null ? 'Change for a site' : 'Change here'}
                              </Button>
                            ),
                          },
                        ]
                      : []),
                  ]}
                  rows={site.rules}
                  rowKey={({ rule }) => `${rule.site ?? ''}|${rule.id}`}
                />
              )}
            </div>
          ))}
        </Card>
      )}

      {editing !== null && (
        <BlockRuleDialog
          scope={editing.kind === 'site' ? { scope: 'server', id: server ?? '' } : ref}
          mode={editing.kind === 'new' ? 'new' : editing.kind === 'edit' ? 'edit' : 'copy'}
          {...(editing.kind === 'new' ? { prefill: editing.prefill } : { rule: editing.rule })}
          {...(editing.kind === 'site' && editing.site !== '' ? { site: editing.site } : {})}
          hosts={effective.data?.hosts ?? []}
          busy={save.isPending}
          error={save.isError ? errorText(save.error, 'Could not save the rule.') : null}
          onSave={(rule) => {
            save.mutate(rule);
          }}
          onCancel={() => {
            setEditing(null);
          }}
        />
      )}

      {removing !== null && (
        <ConfirmDialog
          title={`Remove ${removing.name}?`}
          description={
            removing.site === undefined
              ? 'Every server in this layer stops enforcing it, unless a wider layer has a rule of the same id.'
              : `${removing.site} goes back to the rule of the same id that every other site runs, if there is one.`
          }
          confirmLabel="Remove rule"
          danger
          busy={remove.isPending}
          error={remove.isError ? errorText(remove.error, 'Could not remove it.') : null}
          onConfirm={() => {
            remove.mutate(removing);
          }}
          onCancel={() => {
            setRemoving(null);
          }}
        />
      )}

      {moving !== null && (
        <ConfirmDialog
          title={`Move ${moving.name} up?`}
          description="It leaves this layer and applies to every server of the wider one; a rule of the same id there is replaced."
          confirmLabel="Move rule"
          busy={move.isPending}
          error={move.isError ? errorText(move.error, 'Could not move it.') : null}
          onConfirm={() => {
            move.mutate(moving);
          }}
          onCancel={() => {
            setMoving(null);
          }}
        >
          <SelectField
            label="Move it to"
            value={String(target)}
            onChange={(event) => {
              setTarget(Number(event.target.value));
            }}
          >
            {wider.map((each, index) => (
              <option key={each.label} value={String(index)}>
                {each.label}
              </option>
            ))}
          </SelectField>
        </ConfirmDialog>
      )}
    </div>
  );
}
